import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { widgetAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface WidgetSettings {
  id: number;
  asset_id: number;
  access_key: string;
  widget_score: number;
  widget_status: number;
  widget_online: number;
  widget_metrics: number;
  theme: string;
  size: string;
  is_active: number;
}

const ShareDataPage: React.FC = () => {
  const { currentAsset } = useAuth();
  const [settings, setSettings] = useState<WidgetSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await widgetAPI.getSettings(currentAsset?.id);
      if (res.data?.status === 'success') {
        setSettings(res.data.data.settings);
      }
    } catch (error) {
      toast.error('Failed to load widget settings');
    } finally {
      setLoading(false);
    }
  }, [currentAsset?.id]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSetting = async (field: string, value: any) => {
    if (!settings) return;
    setSaving(true);
    try {
      const res = await widgetAPI.updateSettings({
        asset_id: settings.asset_id,
        [field]: value,
      });
      if (res.data?.status === 'success') {
        setSettings(res.data.data.settings);
        toast.success('Setting updated');
      }
    } catch {
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateKey = async () => {
    if (!settings) return;
    if (!window.confirm('Regenerating the access key will invalidate the old one. Any existing embeds will stop working until updated. Continue?')) return;
    setRegenerating(true);
    try {
      const res = await widgetAPI.regenerateKey(settings.asset_id);
      if (res.data?.status === 'success') {
        setSettings((prev) => prev ? { ...prev, access_key: res.data.data.access_key } : prev);
        toast.success('Access key regenerated');
      }
    } catch {
      toast.error('Failed to regenerate key');
    } finally {
      setRegenerating(false);
    }
  };

  const getEmbedCode = () => {
    if (!settings) return '';
    return `<div data-cloudayn-widget data-key="${settings.access_key}" data-size="${settings.size}" data-theme="${settings.theme}"></div>`;
  };

  const copyToClipboard = (text: string, type: 'code' | 'key') => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'code') {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        setKeyCopied(true);
        setTimeout(() => setKeyCopied(false), 2000);
      }
      toast.success('Copied to clipboard');
    });
  };

  const widgetTypes = [
    {
      key: 'widget_score',
      label: 'Cybersecurity Score',
      description: 'Display your security audit score, grade (A+ to F), and risk level',
      icon: 'bi-shield-check',
      color: '#10b981',
    },
    {
      key: 'widget_status',
      label: 'System Status',
      description: 'Show server online/offline status and uptime percentage',
      icon: 'bi-hdd-rack',
      color: '#3b82f6',
    },
    {
      key: 'widget_online',
      label: 'Online Status',
      description: 'Show whether your website is currently up or down with response time',
      icon: 'bi-broadcast',
      color: '#8b5cf6',
    },
    {
      key: 'widget_metrics',
      label: 'Views & Metrics',
      description: 'Display live visitor count, visitors today, and page views',
      icon: 'bi-graph-up-arrow',
      color: '#6366f1',
    },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const anyEnabled = settings && (settings.widget_score || settings.widget_status || settings.widget_online || settings.widget_metrics);

  return (
    <DashboardLayout>
      <div className="container-fluid px-0">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-1">Share Score & Data</h4>
            <p className="text-muted mb-0">Embed live security scores and metrics on your website</p>
          </div>
          {settings && (
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="widgetActive"
                checked={!!settings.is_active}
                onChange={(e) => updateSetting('is_active', e.target.checked)}
                disabled={saving}
                style={{ width: '3em', height: '1.5em' }}
              />
              <label className="form-check-label ms-2 fw-semibold" htmlFor="widgetActive">
                {settings.is_active ? 'Active' : 'Inactive'}
              </label>
            </div>
          )}
        </div>

        {settings && (
          <>
            {/* Access Key Section */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h6 className="card-title mb-3">
                  <i className="bi bi-key me-2"></i>Access Key
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <code className="bg-light px-3 py-2 rounded flex-grow-1" style={{ fontSize: '13px', wordBreak: 'break-all' }}>
                    {settings.access_key}
                  </code>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => copyToClipboard(settings.access_key, 'key')}
                  >
                    <i className={`bi ${keyCopied ? 'bi-check-lg' : 'bi-clipboard'} me-1`}></i>
                    {keyCopied ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    className="btn btn-outline-warning btn-sm"
                    onClick={handleRegenerateKey}
                    disabled={regenerating}
                  >
                    <i className="bi bi-arrow-repeat me-1"></i>
                    {regenerating ? 'Regenerating...' : 'Regenerate'}
                  </button>
                </div>
                <small className="text-muted mt-2 d-block">
                  This key identifies your widget. Regenerating it will invalidate any existing embeds.
                </small>
              </div>
            </div>

            {/* Widget Toggles */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h6 className="card-title mb-3">
                  <i className="bi bi-toggles me-2"></i>Widget Types
                </h6>
                <div className="row g-3">
                  {widgetTypes.map((w) => {
                    const enabled = !!(settings as any)[w.key];
                    return (
                      <div key={w.key} className="col-md-6">
                        <div
                          className={`border rounded-3 p-3 h-100 ${enabled ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                          style={{ transition: 'all 0.2s' }}
                        >
                          <div className="d-flex align-items-start justify-content-between">
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-3"
                                style={{
                                  width: 44,
                                  height: 44,
                                  backgroundColor: w.color + '15',
                                  color: w.color,
                                  fontSize: '1.3rem',
                                }}
                              >
                                <i className={`bi ${w.icon}`}></i>
                              </div>
                              <div>
                                <div className="fw-semibold">{w.label}</div>
                                <small className="text-muted">{w.description}</small>
                              </div>
                            </div>
                            <div className="form-check form-switch ms-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={enabled}
                                onChange={(e) => updateSetting(w.key, e.target.checked)}
                                disabled={saving}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Appearance */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h6 className="card-title mb-3">
                  <i className="bi bi-palette me-2"></i>Appearance
                </h6>
                <div className="row g-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Theme</label>
                    <div className="btn-group w-100">
                      {['light', 'dark'].map((t) => (
                        <button
                          key={t}
                          className={`btn ${settings.theme === t ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => updateSetting('theme', t)}
                          disabled={saving}
                        >
                          <i className={`bi ${t === 'light' ? 'bi-sun' : 'bi-moon-stars'} me-2`}></i>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Size</label>
                    <div className="btn-group w-100">
                      {['badge', 'card'].map((s) => (
                        <button
                          key={s}
                          className={`btn ${settings.size === s ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => updateSetting('size', s)}
                          disabled={saving}
                        >
                          <i className={`bi ${s === 'badge' ? 'bi-badge-tm' : 'bi-card-heading'} me-2`}></i>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Embed Code */}
            {anyEnabled && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                  <h6 className="card-title mb-3">
                    <i className="bi bi-code-slash me-2"></i>Embed Code
                  </h6>
                  <p className="text-muted mb-3">
                    Copy and paste this tag into your website where you want the widget to appear. No additional script needed — your CloudAyn integration code handles everything.
                  </p>
                  <div className="position-relative">
                    <pre
                      className="bg-dark text-light p-3 rounded-3 mb-0"
                      style={{ fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                    >
                      {getEmbedCode()}
                    </pre>
                    <button
                      className="btn btn-light btn-sm position-absolute"
                      style={{ top: 8, right: 8 }}
                      onClick={() => copyToClipboard(getEmbedCode(), 'code')}
                    >
                      <i className={`bi ${copied ? 'bi-check-lg text-success' : 'bi-clipboard'} me-1`}></i>
                      {copied ? 'Copied!' : 'Copy Code'}
                    </button>
                  </div>
                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      This works automatically with your existing CloudAyn integration script. You can override size and theme using <code>data-size="card"</code> or <code>data-theme="dark"</code> attributes.
                      The widget auto-refreshes every 60 seconds.
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Live Preview */}
            {anyEnabled && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                  <h6 className="card-title mb-3">
                    <i className="bi bi-eye me-2"></i>Preview
                  </h6>
                  <div className="d-flex gap-3 mb-3">
                    <span className={`badge ${settings.theme === 'light' ? 'bg-light text-dark border' : 'bg-dark text-light'}`}>
                      {settings.theme} theme
                    </span>
                    <span className="badge bg-primary">{settings.size} size</span>
                  </div>
                  <div
                    className="rounded-3 p-4"
                    style={{
                      backgroundColor: settings.theme === 'dark' ? '#111827' : '#f8fafc',
                      border: `1px solid ${settings.theme === 'dark' ? '#374151' : '#e5e7eb'}`,
                      minHeight: 120,
                    }}
                  >
                    {settings.size === 'badge' ? (
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        {!!settings.widget_score && (
                          <PreviewBadge
                            theme={settings.theme}
                            icon="bi-shield-check"
                            label="Security Score"
                            value="92/100"
                            valueColor="#10b981"
                            badge="A+"
                            badgeColor="#10b981"
                          />
                        )}
                        {!!settings.widget_online && (
                          <PreviewBadge
                            theme={settings.theme}
                            dot="#10b981"
                            label="Website Status"
                            value="Online - 245ms"
                          />
                        )}
                        {!!settings.widget_status && (
                          <PreviewBadge
                            theme={settings.theme}
                            dot="#10b981"
                            label="System Status"
                            value="3/3 servers - 99.8% uptime"
                          />
                        )}
                        {!!settings.widget_metrics && (
                          <PreviewBadge
                            theme={settings.theme}
                            icon="bi-graph-up-arrow"
                            label="Live Visitors"
                            value="42 now - 1.2K today"
                          />
                        )}
                        <span style={{ fontSize: 10, color: '#9ca3af' }}>CloudAyn</span>
                      </div>
                    ) : (
                      <div style={{ maxWidth: 420 }}>
                        <div
                          className="rounded-3 overflow-hidden"
                          style={{
                            border: `1px solid ${settings.theme === 'dark' ? '#374151' : '#e5e7eb'}`,
                            background: settings.theme === 'dark' ? '#111827' : '#fff',
                          }}
                        >
                          <div
                            className="px-3 py-2 fw-semibold"
                            style={{
                              borderBottom: `1px solid ${settings.theme === 'dark' ? '#374151' : '#f3f4f6'}`,
                              color: settings.theme === 'dark' ? '#e5e7eb' : '#374151',
                              fontSize: 15,
                            }}
                          >
                            {currentAsset?.name || 'Your Website'}
                          </div>
                          <div className="p-3 d-flex flex-column gap-2">
                            {!!settings.widget_score && (
                              <PreviewCard theme={settings.theme} title="Cybersecurity Score">
                                <div className="text-center">
                                  <div style={{ fontSize: 36, fontWeight: 700, color: '#10b981', lineHeight: 1.1 }}>92</div>
                                  <span className="badge" style={{ background: '#10b981', fontSize: 12 }}>A+</span>
                                  <div className="mt-2" style={{ fontSize: 12, color: '#6b7280' }}>Risk: Low | Scanned: 2h ago</div>
                                </div>
                              </PreviewCard>
                            )}
                            {!!settings.widget_online && (
                              <PreviewCard theme={settings.theme} title="Online Status" dot="#10b981">
                                <div className="text-center">
                                  <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>Online</div>
                                  <div style={{ fontSize: 12, color: '#6b7280' }}>Response: 245ms | Checked: 1m ago</div>
                                </div>
                              </PreviewCard>
                            )}
                            {!!settings.widget_status && (
                              <PreviewCard theme={settings.theme} title="System Status" dot="#10b981">
                                <div className="text-center">
                                  <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>Online</div>
                                  <div style={{ fontSize: 12, color: '#6b7280' }}>Servers: 3/3 | Uptime: 99.8%</div>
                                </div>
                              </PreviewCard>
                            )}
                            {!!settings.widget_metrics && (
                              <PreviewCard theme={settings.theme} title="Views & Metrics">
                                <div className="d-flex text-center" style={{ gap: 8 }}>
                                  <div className="flex-fill">
                                    <div style={{ fontSize: 22, fontWeight: 700, color: '#6366f1' }}>42</div>
                                    <div style={{ fontSize: 11, color: '#6b7280' }}>Live Now</div>
                                  </div>
                                  <div className="flex-fill">
                                    <div style={{ fontSize: 22, fontWeight: 700, color: '#6366f1' }}>1.2K</div>
                                    <div style={{ fontSize: 11, color: '#6b7280' }}>Today</div>
                                  </div>
                                  <div className="flex-fill">
                                    <div style={{ fontSize: 22, fontWeight: 700, color: '#6366f1' }}>5.7K</div>
                                    <div style={{ fontSize: 11, color: '#6b7280' }}>Page Views</div>
                                  </div>
                                </div>
                              </PreviewCard>
                            )}
                          </div>
                          <div className="text-center py-2" style={{ fontSize: 11, color: '#9ca3af' }}>
                            Powered by CloudAyn
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!anyEnabled && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body text-center py-5">
                  <i className="bi bi-toggles text-muted" style={{ fontSize: '3rem' }}></i>
                  <h5 className="mt-3">No Widgets Enabled</h5>
                  <p className="text-muted">Enable at least one widget type above to get your embed code.</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

// Preview Badge Component (for inline preview)
const PreviewBadge: React.FC<{
  theme: string;
  icon?: string;
  dot?: string;
  label: string;
  value: string;
  valueColor?: string;
  badge?: string;
  badgeColor?: string;
}> = ({ theme, icon, dot, label, value, valueColor, badge, badgeColor }) => (
  <div
    className="d-flex align-items-center gap-2 px-3 py-2 rounded-3"
    style={{
      background: theme === 'dark' ? '#1f2937' : '#fff',
      border: `1px solid ${theme === 'dark' ? '#374151' : '#e5e7eb'}`,
      minWidth: 180,
    }}
  >
    {dot && (
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: dot, flexShrink: 0 }}></div>
    )}
    {icon && !dot && (
      <i className={`bi ${icon}`} style={{ fontSize: 16, color: valueColor || '#6366f1' }}></i>
    )}
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: valueColor || (theme === 'dark' ? '#f3f4f6' : '#1f2937') }}>{value}</div>
    </div>
    {badge && (
      <span className="badge" style={{ background: badgeColor, color: '#fff', fontSize: 12 }}>{badge}</span>
    )}
  </div>
);

// Preview Card Component (for card preview)
const PreviewCard: React.FC<{
  theme: string;
  title: string;
  dot?: string;
  children: React.ReactNode;
}> = ({ theme, title, dot, children }) => (
  <div
    className="rounded-3"
    style={{
      background: theme === 'dark' ? '#1f2937' : '#f9fafb',
      border: `1px solid ${theme === 'dark' ? '#374151' : '#f3f4f6'}`,
    }}
  >
    <div
      className="px-3 py-2 d-flex align-items-center gap-2"
      style={{ fontSize: 13, fontWeight: 600, color: theme === 'dark' ? '#d1d5db' : '#374151' }}
    >
      {dot && <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot }}></div>}
      {title}
    </div>
    <div className="px-3 pb-3">
      {children}
    </div>
  </div>
);

export default ShareDataPage;
